// src/components/NewAnnouncementDialog.jsx
import { useState, useEffect } from 'react';
import { Send, Loader2, FileText, X, Save } from 'lucide-react';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import SpeechToTextButton from '@/components/SpeechToTextButton';

export default function NewAnnouncementDialog({ open, onOpenChange, onSuccess }) {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [form, setForm] = useState({
    group_ids: [],
    title: '',
    content: ''
  });

  // Load teacher's groups and templates when dialog opens
  useEffect(() => {
    if (open && userProfile) {
      loadGroupsAndTemplates();
    }
  }, [open, userProfile]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setForm({ group_ids: [], title: '', content: '' });
      setSelectedTemplateId('');
      setSaveAsTemplate(false);
      setTemplateName('');
    }
  }, [open]);

  const loadGroupsAndTemplates = async () => {
    setLoading(true);
    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      if (!branchId) {
        setGroups([]);
        setTemplates([]);
        return;
      }

      // Load groups and templates in parallel
      const [groupsResult, templatesResult] = await Promise.all([
        supabase
          .from('groups')
          .select('*')
          .eq('branch_id', branchId),
        supabase
          .from('templates')
          .select('*')
          .eq('branch_id', branchId)
          .eq('type', 'announcement')
          .order('created_at', { ascending: false })
      ]);

      if (groupsResult.error) throw groupsResult.error;

      // Filter groups where teacher is main or secondary
      const teacherGroups = groupsResult.data?.filter(g => {
        const isMainTeacher = g.main_teacher_id === userProfile.id;
        const isInGroupTeacherIds = g.teacher_ids?.includes(userProfile.id);
        const isInUserGroupIds = userProfile.group_ids?.includes(g.id);
        return isMainTeacher || isInGroupTeacherIds || isInUserGroupIds;
      }) || [];

      setGroups(teacherGroups);
      setTemplates(templatesResult.data || []);
    } catch (e) {
      logger.error('Failed to load data:', e);
      toast.error('Nie udalo sie zaladowac danych');
    } finally {
      setLoading(false);
    }
  };

  // Apply template to form
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template?.template_data) {
        setForm(prev => ({
          ...prev,
          title: template.template_data.title || '',
          content: template.template_data.content || ''
        }));
      }
    }
  };

  // Clear template selection
  const handleClearTemplate = () => {
    setSelectedTemplateId('');
    setForm(prev => ({ ...prev, title: '', content: '' }));
  };

  const handleSendAnnouncement = async () => {
    if (form.group_ids.length === 0) {
      toast.error('Wybierz co najmniej jedna grupe');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Podaj tytul ogloszenia');
      return;
    }
    if (!form.content.trim()) {
      toast.error('Podaj tresc ogloszenia');
      return;
    }
    if (saveAsTemplate && !templateName.trim()) {
      toast.error('Podaj nazwe szablonu');
      return;
    }

    setSending(true);
    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];

      // Create one announcement per group
      const announcementsToInsert = form.group_ids.map(groupId => ({
        branch_id: branchId,
        group_id: groupId,
        title: form.title.trim(),
        content: form.content.trim(),
        sender_id: userProfile.id,
        type: 'announcement',
        priority: 'normal',
        read_by: [],
        published_at: new Date().toISOString()
      }));

      const { data: insertedAnnouncements, error } = await supabase
        .from('announcements')
        .insert(announcementsToInsert)
        .select('id, group_id');

      if (error) throw error;

      // Create notifications for co-teachers only
      // NOTE: Parent notifications are handled by database trigger (trg_announcement_notify_parents)
      try {
        // Get groups with their teacher assignments
        const { data: groupsData } = await supabase
          .from('groups')
          .select('id, name, main_teacher_id, teacher_ids')
          .in('id', form.group_ids);

        // Collect co-teacher IDs (excluding sender)
        const coTeacherIds = new Set();
        groupsData?.forEach(group => {
          if (group.main_teacher_id && group.main_teacher_id !== userProfile.id) {
            coTeacherIds.add(group.main_teacher_id);
          }
          group.teacher_ids?.forEach(tid => {
            if (tid !== userProfile.id) coTeacherIds.add(tid);
          });
        });

        // Build notifications for co-teachers only
        const notifications = [];

        coTeacherIds.forEach(teacherId => {
          notifications.push({
            user_id: teacherId,
            type: 'announcement_sent',
            title: 'Nowe ogłoszenie w grupie',
            message: `${userProfile.first_name || 'Nauczyciel'} wysłał(a) ogłoszenie: ${form.title.trim()}`,
            priority: 'low',
            read: false,
            metadata: {
              group_ids: form.group_ids,
              announcement_ids: insertedAnnouncements?.map(a => a.id) || [],
              sender_id: userProfile.id
            }
          });
        });

        // Bulk insert notifications (ignore errors - non-critical)
        if (notifications.length > 0) {
          await supabase.from('notifications').insert(notifications);
          logger.debug('Co-teacher notifications created', { count: notifications.length });
        }
      } catch (notifError) {
        // Non-critical - log but don't fail the announcement
        logger.error('Failed to create co-teacher notifications:', notifError);
      }

      // Also save as template if enabled
      if (saveAsTemplate && templateName.trim()) {
        const { error: templateError } = await supabase
          .from('templates')
          .insert({
            name: templateName.trim(),
            type: 'announcement',
            template_data: {
              title: form.title.trim(),
              content: form.content.trim()
            },
            branch_id: branchId,
            created_by: userProfile.id
          });

        if (templateError) {
          logger.error('Failed to save template:', templateError);
          toast.error('Ogłoszenie wysłane, ale nie udało się zapisać szablonu');
        } else {
          toast.success(`Ogłoszenie wysłane do ${form.group_ids.length} grup i zapisane jako szablon`);
        }
      } else {
        toast.success(`Ogloszenie wyslane do ${form.group_ids.length} grup`);
      }

      onOpenChange(false);
      setForm({ group_ids: [], title: '', content: '' });
      setSaveAsTemplate(false);
      setTemplateName('');
      onSuccess?.();
    } catch (error) {
      logger.error('Failed to send announcement:', error);
      toast.error('Nie udalo sie wyslac ogloszenia');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nowe Ogloszenie</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Nie jestes przypisany do zadnej grupy.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Template Selection */}
            {templates.length > 0 && (
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Użyj szablonu (opcjonalnie)
                </Label>
                <div className="flex gap-2">
                  <Select value={selectedTemplateId} onValueChange={handleTemplateSelect}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Wybierz szablon..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplateId && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleClearTemplate}
                      className="shrink-0"
                      title="Wyczyść szablon"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {selectedTemplateId && (
                  <p className="text-xs text-blue-600 mt-1">
                    Szablon zastosowany - możesz edytować pola poniżej
                  </p>
                )}
              </div>
            )}

            {/* Group Selection (Multi) */}
            <div>
              <Label className="mb-2 block">Wybierz grupy</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                {/* Select All */}
                <label className="flex items-center gap-2 cursor-pointer pb-2 border-b">
                  <Checkbox
                    checked={form.group_ids.length === groups.length}
                    onCheckedChange={(checked) => {
                      setForm(prev => ({
                        ...prev,
                        group_ids: checked ? groups.map(g => g.id) : []
                      }));
                    }}
                  />
                  <span className="text-sm font-medium">Wszystkie grupy</span>
                </label>
                {groups.map(group => (
                  <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.group_ids.includes(group.id)}
                      onCheckedChange={(checked) => {
                        setForm(prev => ({
                          ...prev,
                          group_ids: checked
                            ? [...prev.group_ids, group.id]
                            : prev.group_ids.filter(id => id !== group.id)
                        }));
                      }}
                    />
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: group.color_scheme || '#6366f1' }}
                    />
                    <span className="text-sm">{group.name}</span>
                  </label>
                ))}
              </div>
              {form.group_ids.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Wybrano {form.group_ids.length} z {groups.length} grup
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="announcement-title">Tytul</Label>
                <SpeechToTextButton
                  currentValue={form.title}
                  onResult={(t) => setForm(prev => ({ ...prev, title: t }))}
                  append={false}
                  size="icon"
                />
              </div>
              <Input
                id="announcement-title"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Tytul ogloszenia"
                className="mt-1"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="announcement-content">Tresc</Label>
                <SpeechToTextButton
                  currentValue={form.content}
                  onResult={(t) => setForm(prev => ({ ...prev, content: t }))}
                  append
                  size="icon"
                />
              </div>
              <Textarea
                id="announcement-content"
                value={form.content}
                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Tresc ogloszenia..."
                rows={5}
                className="mt-1"
              />
            </div>

            {/* Save as Template Option */}
            <div className="border-t pt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={saveAsTemplate}
                  onCheckedChange={(checked) => {
                    setSaveAsTemplate(checked);
                    if (!checked) setTemplateName('');
                  }}
                />
                <Save className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Zapisz jako szablon</span>
              </label>
              {saveAsTemplate && (
                <div className="mt-3 ml-6">
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Nazwa szablonu, np. Informacja o wycieczce"
                    className="text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Szablon będzie dostępny przy tworzeniu przyszłych ogłoszeń
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            onClick={handleSendAnnouncement}
            disabled={sending || loading || groups.length === 0 || !form.title.trim() || !form.content.trim() || form.group_ids.length === 0 || (saveAsTemplate && !templateName.trim())}
            className="bg-gradient-to-r from-purple-500 to-violet-600"
          >
            {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Send className="w-4 h-4 mr-2" />
            {saveAsTemplate ? 'Wyślij i zapisz szablon' : 'Wyślij'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
